// ui.store.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'light' | 'dark' | 'system';
export type DateFormat =
  | 'dd/MM/yyyy'
  | 'MM/dd/yyyy'
  | 'yyyy-MM-dd'
  | 'dd-MM-yyyy'
  | 'MM-dd-yyyy'
  | 'dd.MM.yyyy'
  | 'd MMM yyyy';
export type TimeFormat = 'HH:mm' | 'HH:mm:ss' | 'hh:mm a' | 'hh:mm:ss a';

export type ActivePanelId =
  | 'home'
  | 'projects'
  | 'analytics'
  | 'notes'
  | 'calendar'
  | 'messages'
  | 'inbox'
  | 'settings';

export type SettingsSectionId =
  | 'profile'
  | 'security'
  | 'notifications'
  | 'integrations'
  | 'org-info'
  | 'org-members'
  | 'org-permissions'
  | 'org-general'
  | 'org-work-schedule'
  | 'project-config'
  | 'project-members'
  | 'project-permissions'
  | 'project-settings'
  | 'project-statuses';

/** IDs de sección válidos — usado para sanitizar valores persistidos en localStorage */
export const VALID_SETTINGS_SECTION_IDS: SettingsSectionId[] = [
  'profile',
  'security',
  'notifications',
  'integrations',
  'org-info',
  'org-members',
  'org-permissions',
  'org-general',
  'org-work-schedule',
  'project-config',
  'project-members',
  'project-permissions',
  'project-settings',
  'project-statuses',
];

const applyTheme = (theme: Theme) => {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else if (theme === 'light') {
    root.classList.remove('dark');
  } else {
    const isSystemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (isSystemDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }
};

interface UIState {
  sidebarOpen: boolean;
  mobileSidebarOpen: boolean;
  theme: Theme;
  timezone: string;
  dateFormat: DateFormat;
  timeFormat: TimeFormat;
  activePanelId: ActivePanelId;
  activeSettingsSectionId: SettingsSectionId;
  selectedProjectId: string | null;

  toggleSidebar: () => void;
  toggleMobileSidebar: () => void;
  closeMobileSidebar: () => void;
  setTheme: (theme: Theme) => void;
  setTimezone: (tz: string) => void;
  setDateFormat: (format: DateFormat) => void;
  setTimeFormat: (format: TimeFormat) => void;
  setActivePanelId: (id: ActivePanelId) => void;
  setActiveSettingsSectionId: (id: SettingsSectionId) => void;
  setSelectedProjectId: (id: string | null) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarOpen: true,
      mobileSidebarOpen: false,
      theme: 'system',
      timezone:
        typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC',
      dateFormat: 'dd/MM/yyyy',
      timeFormat: 'HH:mm',
      activePanelId: 'home',
      activeSettingsSectionId: 'profile',
      selectedProjectId: null,

      toggleSidebar: () =>
        set((state) => ({
          sidebarOpen: !state.sidebarOpen,
        })),
      toggleMobileSidebar: () => set((state) => ({ mobileSidebarOpen: !state.mobileSidebarOpen })),
      closeMobileSidebar: () => set({ mobileSidebarOpen: false }),
      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },
      setTimezone: (tz) => set({ timezone: tz }),
      setDateFormat: (format) => set({ dateFormat: format }),
      setTimeFormat: (format) => set({ timeFormat: format }),
      setActivePanelId: (id) => set({ activePanelId: id }),
      setActiveSettingsSectionId: (id) => set({ activeSettingsSectionId: id }),
      setSelectedProjectId: (id) => set({ selectedProjectId: id }),
    }),
    {
      name: 'khronox-ui',
      partialize: (state) => ({
        theme: state.theme,
        timezone: state.timezone,
        dateFormat: state.dateFormat,
        timeFormat: state.timeFormat,
        sidebarOpen: state.sidebarOpen,
        activeSettingsSectionId: state.activeSettingsSectionId,
      }),
    },
  ),
);

if (typeof window !== 'undefined') {
  const stored = localStorage.getItem('khronox-ui');
  if (stored) {
    try {
      const { state } = JSON.parse(stored);
      if (state?.theme) {
        applyTheme(state.theme);
      }
    } catch {
      applyTheme('system');
    }
  } else {
    applyTheme('system');
  }
}
